import {
  Banner,
  BlockStack,
  Card,
  Spinner,
  Text,
  TextField,
} from '@shopify/polaris';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { api, PaywallError } from '../lib/api.js';
import type { ComplianceIssue } from '../lib/types.js';
import { AppPage } from '../components/Layout.js';
import { SeverityBadge } from '../components/SeverityBadge.js';
import { formatPublicRegulations } from '../lib/regulation-labels.js';

/**
 * Design #06 — Product detail. Shows issue diagnosis, metric cards,
 * fix suggestion, and action buttons (disclosure, rewrite, skip).
 */
export function ProductDetail(): JSX.Element {
  const { t } = useTranslation();
  const { issueId } = useParams<{ issueId: string }>();
  const navigate = useNavigate();

  const [issue, setIssue] = useState<ComplianceIssue | null>(null);
  const [applying, setApplying] = useState(false);
  const [rewriting, setRewriting] = useState(false);
  const [rewrite, setRewrite] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [paywall, setPaywall] = useState(false);
  const [applied, setApplied] = useState(false);
  const [postFixScore, setPostFixScore] = useState<number | null>(null);

  useEffect(() => {
    if (!issueId) return;
    const ac = new AbortController();
    (async () => {
      try {
        const latest = await api.get<{ scan: { id: string } | null }>('/api/scan/latest', ac.signal);
        if (!latest.scan) return;
        const page = await api.get<{ items: ComplianceIssue[] }>(
          `/api/scan/${latest.scan.id}/findings?limit=100`,
          ac.signal,
        );
        const found = page.items.find((i) => i.id === issueId);
        if (found) setIssue(found);
        else setError('issue_not_found');
      } catch (e) {
        if ((e as Error).name === 'AbortError') return;
        setError((e as Error).message);
      }
    })();
    return () => ac.abort();
  }, [issueId]);

  const applyDisclosure = useCallback(async () => {
    if (!issue) return;
    setApplying(true);
    setError(null);
    try {
      const r = await api.post<{ newScore?: number }>(`/api/fix/apply/${issue.id}`);
      setApplied(true);
      if (typeof r.newScore === 'number') setPostFixScore(r.newScore);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setApplying(false);
    }
  }, [issue]);

  const loadCurrentDescription = useCallback(async () => {
    if (!issue) return;
    setRewriting(true);
    setError(null);
    try {
      const r = await api.get<{ currentText: string }>(`/api/rewrite/${issue.id}/source`);
      setRewrite(r.currentText);
    } catch (e) {
      if (e instanceof PaywallError) {
        setPaywall(true);
      } else {
        setError((e as Error).message);
      }
    } finally {
      setRewriting(false);
    }
  }, [issue]);

  const applyRewriteAction = useCallback(async () => {
    if (!issue || !rewrite) return;
    setApplying(true);
    setError(null);
    try {
      const r = await api.post<{ newScore?: number }>(
        `/api/rewrite/${issue.id}/apply`,
        { text: rewrite },
      );
      setApplied(true);
      if (typeof r.newScore === 'number') setPostFixScore(r.newScore);
    } catch (e) {
      if (e instanceof PaywallError) setPaywall(true);
      else setError((e as Error).message);
    } finally {
      setApplying(false);
    }
  }, [issue, rewrite]);

  if (!issue) {
    return (
      <AppPage
        title="…"
        backAction={{ content: t('productDetail.back'), onAction: () => navigate('/', applied ? { state: { justFixed: true } } : undefined) }}
      >
        {error ? (
          <Banner tone="critical">{error}</Banner>
        ) : (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
            <Spinner accessibilityLabel="Loading" />
          </div>
        )}
      </AppPage>
    );
  }

  const regLabel = formatPublicRegulations(issue.regulations, t);
  // Once the fix is applied and the server echoes the projected post-fix
  // score, we recolor the score card green and show `before → after` to match
  // the dashboard's `FixedRow` rendering — previously the card kept the
  // pre-fix number, which read as "nothing happened".
  const scoreTone =
    postFixScore != null
      ? 'success'
      : issue.aiScore >= 70
        ? 'critical'
        : issue.aiScore >= 45
          ? 'warning'
          : 'success';
  const scoreToneColor = scoreTone === 'critical' ? '#D72C0D' : scoreTone === 'warning' ? '#B98900' : '#008060';

  return (
    <AppPage>
      {/* Back link — design .back-link */}
      <button
        type="button"
        onClick={() => navigate('/', applied ? { state: { justFixed: true } } : undefined)}
        style={{
          background: 'none', border: 'none', padding: 0, marginBottom: 12,
          fontSize: 13, color: '#005BD3', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
        }}
      >
        ← {t('productDetail.back')}
      </button>

      <div style={{ fontSize: 20, fontWeight: 600, marginBottom: 4 }}>{issue.productTitle}</div>
      <div style={{ fontSize: 13, color: '#6D7175', marginBottom: 16 }}>
        {postFixScore != null
          ? `${t('productDetail.scoreLabel')}: ${issue.aiScore} → ${postFixScore}`
          : t('productDetail.riskScore', { score: issue.aiScore })}
      </div>

      <BlockStack gap="400">
        {paywall && (
          <Banner tone="warning" title="Upgrade required" onDismiss={() => setPaywall(false)}>
            <p>Manual rewrite requires the Full Report plan.</p>
          </Banner>
        )}
        {error && (
          <Banner tone="critical" onDismiss={() => setError(null)}>{error}</Banner>
        )}

        {/* Success banner — design green checkmark bar */}
        {applied && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px',
            borderRadius: 12, background: '#F1F8F4', border: '1px solid #98C6A7',
          }}>
            <div style={{
              width: 22, height: 22, borderRadius: '50%', background: '#008060',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', fontSize: 12, fontWeight: 700, flexShrink: 0,
            }}>✓</div>
            <span style={{ fontSize: 13, color: '#008060', fontWeight: 600 }}>
              {t('productDetail.disclosureApplied')}
            </span>
          </div>
        )}

        {/* 3-column metric cards — design .metrics-grid-3 */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
          <div style={{ background: 'var(--p-color-bg-surface-secondary)', borderRadius: 8, padding: '12px 14px' }}>
            <div style={{ fontSize: 12, color: '#6D7175', marginBottom: 4 }}>{t('productDetail.scoreLabel')}</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: scoreToneColor }}>
              {postFixScore != null
                ? `${issue.aiScore} → ${postFixScore}`
                : `${issue.aiScore}/100`}
            </div>
          </div>
          <div style={{ background: 'var(--p-color-bg-surface-secondary)', borderRadius: 8, padding: '12px 14px' }}>
            <div style={{ fontSize: 12, color: '#6D7175', marginBottom: 4 }}>{t('productDetail.statusLabel')}</div>
            <div style={{ marginTop: 4 }}><SeverityBadge severity={issue.severity} /></div>
          </div>
          <div style={{ background: 'var(--p-color-bg-surface-secondary)', borderRadius: 8, padding: '12px 14px' }}>
            <div style={{ fontSize: 12, color: '#6D7175', marginBottom: 4 }}>{t('productDetail.regulationLabel')}</div>
            <div style={{ fontSize: 13, fontWeight: 600, marginTop: 4 }}>{regLabel}</div>
          </div>
        </div>

        {/* Issue detected card — design .banner-critical inside .card */}
        <Card>
          <BlockStack gap="300">
            <Text as="h3" variant="headingMd">{t('productDetail.issueDetected')}</Text>
            <div style={{
              borderRadius: 12, padding: '14px 16px',
              background: '#FFF4F4', border: '1px solid #E4A5A5',
            }}>
              <div style={{ fontSize: 13, color: '#D72C0D', lineHeight: 1.5 }}>
                {issue.fixSuggestion ?? 'Description shows strong AI-generation patterns.'}
              </div>
            </div>
            <Text as="p" tone="subdued" variant="bodySm">
              {t('productDetail.regulationContext')}
            </Text>
          </BlockStack>
        </Card>

        {/* Recommended fix card — design .fix-box */}
        <Card>
          <BlockStack gap="300">
            <Text as="h3" variant="headingMd">{t('productDetail.recommendedFix')}</Text>
            <div style={{
              background: '#F1F8F4', border: '1px solid #98C6A7',
              borderRadius: 10, padding: '14px 16px',
            }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#008060', marginBottom: 4 }}>
                {t('productDetail.addDisclosureLabel')}
              </div>
              <div style={{ fontSize: 13, color: '#008060', lineHeight: 1.5 }}>
                {t('productDetail.appendToDescription', { text: 'Product description created with AI assistance.' })}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <BluePrimaryBtn loading={applying} disabled={applied} onClick={applyDisclosure}>
                {t('productDetail.applyDisclosure')}
              </BluePrimaryBtn>
              <OutlineDetailBtn loading={rewriting} onClick={loadCurrentDescription}>
                {t('productDetail.rewriteDescription')}
              </OutlineDetailBtn>
              <button
                type="button"
                onClick={() => navigate('/', applied ? { state: { justFixed: true } } : undefined)}
                style={{
                  background: 'none', border: 'none', padding: '8px 4px',
                  fontSize: 13, color: '#005BD3', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                }}
              >
                {t('productDetail.skipProduct')}
              </button>
            </div>
          </BlockStack>
        </Card>

        {/* Rewrite preview */}
        {rewrite && (
          <Card>
            <BlockStack gap="300">
              <Text as="h3" variant="headingMd">{t('productDetail.rewritePreviewTitle')}</Text>
              <TextField
                label=""
                labelHidden
                value={rewrite}
                onChange={setRewrite}
                multiline={6}
                autoComplete="off"
              />
              {/* The TextField above is already editable inline, so no
                  separate "Edit before applying" action is needed — a merchant
                  can tweak the copy directly and then hit Apply. */}
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <BluePrimaryBtn
                  loading={applying}
                  disabled={applied}
                  onClick={applyRewriteAction}
                >
                  {t('productDetail.applyRewrite')}
                </BluePrimaryBtn>
              </div>
            </BlockStack>
          </Card>
        )}
      </BlockStack>
    </AppPage>
  );
}

/** Blue primary button matching design .btn-primary */
function BluePrimaryBtn({ children, onClick, loading, disabled }: {
  children: React.ReactNode; onClick: () => void; loading?: boolean; disabled?: boolean;
}): JSX.Element {
  const [hover, setHover] = useState(false);
  return (
    <button type="button" onClick={onClick} disabled={loading || disabled}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{
        background: hover && !disabled ? '#004BB0' : '#005BD3', color: '#fff', border: 'none',
        borderRadius: 8, padding: '8px 18px', fontSize: 13, fontWeight: 600,
        cursor: disabled ? 'default' : 'pointer', fontFamily: 'inherit',
        opacity: disabled ? 0.5 : 1, transition: 'background 0.15s',
      }}>
      {loading ? 'Applying…' : children}
    </button>
  );
}

/** Outline button matching design .btn */
function OutlineDetailBtn({ children, onClick, loading }: {
  children: React.ReactNode; onClick: () => void; loading?: boolean;
}): JSX.Element {
  const [hover, setHover] = useState(false);
  return (
    <button type="button" onClick={onClick} disabled={loading}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{
        background: hover ? '#F6F6F7' : '#fff', color: '#303030',
        border: '1px solid #C9CCCF', borderRadius: 8, padding: '8px 18px',
        fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
        transition: 'background 0.15s',
      }}>
      {loading ? 'Loading…' : children}
    </button>
  );
}
